import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * FittingBox VTO Advanced — Native Frame Carousel only (no custom color UI).
 *
 * Carousel JSON is built like the PDP color picker (one frame per color).
 * Passed at createWidget + via setCarousel() before startVto.
 *
 * @see https://fittingbox.com/en/resources/help-center/how-to-configure-vto-native-frame-carousel-
 */

const FITMIX_SCRIPT_URL = '//vto-advanced-integration-api.fittingbox.com/index.js';
const FITMIX_CONTAINER_ID = 'my-fitmix-container';
const VTO_RECOMMENDATIONS_SECTION_ID = 'vto-recommendations';
const VTO_RECOMMENDATIONS_TIMEOUT_MS = 800;

/**
 * Passed via createWidget's `css` param (FitMix's own documented style-override
 * hook - our theme CSS can't reach inside the widget directly). The color swatch
 * is an SVG circle with a pattern-filled image; there's no element to turn into a
 * rectangle, so the circle is enlarged past its box and the box is clipped to a
 * rounded rect instead - same visual result, a bit bigger.
 */
const VTO_SWATCH_CSS = `
  vto-advanced-variant-color-button {
    width: 64px;
    height: 64px;
    border-radius: 8px;
    border: 1px solid transparent;
    margin-right: 12px;
    background: #fff;
    cursor: pointer;

  }
  @media (min-width: 768px) {
    vto-advanced-variant-color-button {
      width: 64px !important;
      height: 64px !important;
    }
  }
  vto-advanced-variant-color-button [data-testid="variant-color-button"] {
    border-radius: 8px !important;
  }
  vto-advanced-variant-color-button [data-testid="svg-variant-button"] {
    clip-path: inset(0% round 8px);
  }
  vto-advanced-variant-color-button [data-testid="circle"] {
    r: 16;
  }
  vto-advanced-variant-color-button [data-testid="image"] {
    object-fit: contain;
  }
`;

/**
 * Same style-override hook as VTO_SWATCH_CSS, for the "you might also like" frame
 * cards. The card's second line is `frameAdditionalInfo`, which our carousel JSON
 * never sends (see vto-carousel-frame-entry) - FittingBox fills it with the brand
 * only for frames it recognises from its own catalog. When blank it still reserves
 * its line height, so collapse it. `:empty` keeps the brand line when it IS filled.
 */
const VTO_GLASS_CARD_CSS = `
  vto-advanced-shared-glass-card p:empty {
    display: none;
  }
`;

/** @type {Promise<any> | null} */
let fitMixLoader = null;

function loadFitMix() {
  if (window.FitMix) return Promise.resolve(window.FitMix);
  if (fitMixLoader) return fitMixLoader;

  fitMixLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="fittingbox.com/index.js"]');
    if (existing) {
      if (window.FitMix) {
        resolve(window.FitMix);
        return;
      }
      existing.addEventListener('load', () => resolve(window.FitMix));
      existing.addEventListener('error', () => reject(new Error('FitMix script failed')));
      return;
    }

    const script = document.createElement('script');
    script.src = FITMIX_SCRIPT_URL;
    script.onload = () => resolve(window.FitMix);
    script.onerror = () => reject(new Error('FitMix script failed'));
    document.head.appendChild(script);
  });

  return fitMixLoader;
}

function afterLayout() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/**
 * @param {unknown} raw
 * @returns {object | null}
 */
function parseCarousel(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return /** @type {object} */ (raw);
  if (typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

/**
 * Put the active frameId’s model first (FittingBox highlights the first / current frame).
 * @param {object} carousel
 * @param {string} frameId
 */
function withActiveFrameFirst(carousel, frameId) {
  if (!carousel?.frames?.length || !frameId) return carousel;

  const frames = carousel.frames.slice();
  const index = frames.findIndex((frame) =>
    Array.isArray(frame?.variants) &&
    frame.variants.some(
      (variant) =>
        Array.isArray(variant?.sizes) && variant.sizes.some((size) => String(size.frameId) === String(frameId))
    )
  );
  if (index <= 0) return carousel;

  const [active] = frames.splice(index, 1);
  frames.unshift(active);
  return { ...carousel, frames };
}

/**
 * Normalize to FitMix carousel Zod schema (Oh).
 * @param {object | null} carousel
 * @param {string} frameId
 * @returns {object | null}
 */
function normalizeCarousel(carousel, frameId) {
  if (!carousel?.frames?.length) return null;

  const ordered = withActiveFrameFirst(carousel, frameId);
  const frames = [];

  for (const frame of ordered.frames.slice(0, 6)) {
    if (!frame?.frameLabel || !frame?.frameImageUrl || !Array.isArray(frame.variants) || !frame.variants.length) {
      continue;
    }

    const variants = [];
    for (const variant of frame.variants) {
      if (!Array.isArray(variant?.sizes) || !variant.sizes.length) continue;

      const sizes = [];
      for (const size of variant.sizes) {
        if (!size?.frameId) continue;
        /** @type {{ frameId: string, sizeLabel?: string }} */
        const entry = { frameId: String(size.frameId) };
        if (size.sizeLabel) entry.sizeLabel = String(size.sizeLabel);
        sizes.push(entry);
      }
      if (!sizes.length) continue;

      /** @type {{ sizes: object[], variantLabel?: string, variantThumbnailUrl?: string }} */
      const colorVariant = { sizes };
      if (variant.variantLabel) colorVariant.variantLabel = String(variant.variantLabel);
      if (variant.variantThumbnailUrl) colorVariant.variantThumbnailUrl = String(variant.variantThumbnailUrl);
      variants.push(colorVariant);
    }

    if (!variants.length) continue;

    /** @type {{ frameLabel: string, frameImageUrl: string, variants: object[], frameAdditionalInfo?: string }} */
    const next = {
      frameLabel: String(frame.frameLabel),
      frameImageUrl: String(frame.frameImageUrl),
      variants,
    };
    if (frame.frameAdditionalInfo) next.frameAdditionalInfo = String(frame.frameAdditionalInfo);
    frames.push(next);
  }

  if (!frames.length) return null;

  /** @type {{ frames: object[], settings?: { recommendedSectionTitle?: string }, lenses?: object[] }} */
  const result = { frames };
  if (carousel.settings && typeof carousel.settings === 'object') {
    result.settings = {};
    if (carousel.settings.recommendedSectionTitle) {
      result.settings.recommendedSectionTitle = String(carousel.settings.recommendedSectionTitle);
    }
  }
  return result;
}

export class VtoTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick, { capture: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick, { capture: true });
  }

  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    let frameId = this.dataset.sapid || '';

    if (this.dataset.variantSapids) {
      const input = document.querySelector('product-form-component input[name="id"]');
      const variantId = input instanceof HTMLInputElement ? input.value : '';
      try {
        const map = JSON.parse(this.dataset.variantSapids);
        if (variantId && map[variantId] != null) frameId = String(map[variantId]);
      } catch (_) {}
    }

    if (!frameId) return;

    const script = this.querySelector('script[type="application/json"][data-vto-carousel]');
    const carousel = parseCarousel(script?.textContent?.trim() || this.dataset.carousel || '');

    document.dispatchEvent(
      new CustomEvent('vto:open', {
        bubbles: true,
        detail: {
          frameId,
          productId: this.dataset.productId || '',
          variantSapids: this.dataset.variantSapids || '',
          carousel,
        },
      })
    );
  };
}

const activeStreams = new Set();

if (navigator.mediaDevices?.getUserMedia && !navigator.mediaDevices.getUserMedia.__vtoTracked) {
  const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
  const trackedGetUserMedia = async (...args) => {
    const stream = await originalGetUserMedia(...args);
    activeStreams.add(stream);
    return stream;
  };
  trackedGetUserMedia.__vtoTracked = true;
  navigator.mediaDevices.getUserMedia = trackedGetUserMedia;
}

export class VtoDialog extends DialogComponent {
  /** @type {any} */
  #fitmix = null;

  /** @type {boolean} */
  #fitmixReady = false;

  /** @type {Array<() => void>} */
  #readyQueue = [];

  /** @type {Record<string, string>} */
  #variantFrameIds = {};

  /** @type {boolean} */
  #closing = false;

  /** @type {number} */
  #openGeneration = 0;

  /** @type {string} */
  #carouselKey = '';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('vto:open', this.#onOpen);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('vto:open', this.#onOpen);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
  }

  /**
   * @param {CustomEvent} event
   */
  #onOpen = async (event) => {
    const generation = ++this.#openGeneration;
    const frameId = String(event.detail?.frameId || event.detail?.sapid || '');
    if (!frameId) return;

    this.#variantFrameIds = {};
    if (event.detail?.variantSapids) {
      try {
        this.#variantFrameIds = JSON.parse(event.detail.variantSapids);
      } catch (_) {}
    }

    const productId = String(event.detail?.productId || '');
    const ownCarousel = normalizeCarousel(event.detail?.carousel || null, frameId);

    this.showDialog();
    await afterLayout();
    if (generation !== this.#openGeneration) return;

    const carousel = await this.#withRecommendedFrames(ownCarousel, productId, frameId);
    if (generation !== this.#openGeneration) return;

    try {
      await this.#ensureFitMix(carousel, frameId);
      if (generation !== this.#openGeneration) return;
      await this.#whenFitMixReady();
      if (generation !== this.#openGeneration) return;
      this.#startExperience(frameId, carousel);
    } catch (_) {
      this.closeDialog();
    }
  };

  /**
   * Extends this product's own carousel with FittingBox frames built from Shopify's
   * product recommendations, so the native "You might also like" panel covers other
   * products, not just this product's own colors. Additive and non-critical: a slow
   * or failed fetch (bounded by VTO_RECOMMENDATIONS_TIMEOUT_MS) just falls back to
   * the product's own carousel.
   * @param {object | null} ownCarousel
   * @param {string} productId
   * @param {string} frameId
   * @returns {Promise<object | null>}
   */
  async #withRecommendedFrames(ownCarousel, productId, frameId) {
    const url = Theme.routes?.product_recommendations_url;
    if (!url || !productId) return ownCarousel;

    const additions = await Promise.race([
      this.#fetchRecommendedFrames(url, productId),
      new Promise((resolve) => setTimeout(() => resolve(null), VTO_RECOMMENDATIONS_TIMEOUT_MS)),
    ]).catch(() => null);

    if (!additions?.length) return ownCarousel;

    const existingFrameIds = new Set(
      (ownCarousel?.frames || []).flatMap((frame) =>
        frame.variants.flatMap((variant) => variant.sizes.map((size) => size.frameId))
      )
    );
    const newFrames = additions.filter(
      (frame) => !frame.variants?.some((variant) => variant.sizes?.some((size) => existingFrameIds.has(size.frameId)))
    );
    if (!newFrames.length) return ownCarousel;

    return normalizeCarousel({ ...ownCarousel, frames: [...(ownCarousel?.frames || []), ...newFrames] }, frameId);
  }

  /**
   * @param {string} url
   * @param {string} productId
   * @returns {Promise<object[] | null>}
   */
  async #fetchRecommendedFrames(url, productId) {
    const response = await fetch(
      `${url}?limit=5&intent=related&product_id=${productId}&section_id=${VTO_RECOMMENDATIONS_SECTION_ID}`
    );
    if (!response.ok) return null;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const script = doc.querySelector('script[data-vto-recommendations-carousel]');
    const parsed = parseCarousel(script?.textContent?.trim() || '');
    return Array.isArray(parsed?.frames) ? parsed.frames : null;
  }

  /**
   * Create once; recreate only when carousel payload changes so native panel always gets fresh init data.
   * @param {object | null} [initialCarousel]
   * @param {string} [frameId]
   */
  async #ensureFitMix(initialCarousel = null, frameId = '') {
    const nextKey = initialCarousel ? JSON.stringify(initialCarousel) : '';

    if (this.#fitmix && this.#carouselKey === nextKey) return;

    if (this.#fitmix) {
      try {
        this.#fitmix.remove?.();
      } catch (_) {}
      this.#fitmix = null;
      this.#fitmixReady = false;
      this.#readyQueue = [];
      const container = this.querySelector(`#${FITMIX_CONTAINER_ID}`);
      if (container) container.innerHTML = '';
    }

    const FitMix = await loadFitMix();
    const container = this.querySelector(`#${FITMIX_CONTAINER_ID}`);
    if (!(container instanceof HTMLElement)) throw new Error('FitMix container missing');

    const rect = container.getBoundingClientRect();
    const params = {
      apiKey: this.dataset.apiKey || '',
      lang: this.dataset.locale || 'en',
      width: Math.max(320, Math.floor(rect.width) || 900),
      height: Math.max(400, Math.floor(rect.height) || 700),
      css: VTO_SWATCH_CSS + VTO_GLASS_CARD_CSS,
      onStopVto: () => {
        this.#releaseCamera();
        if (this.#closing) return;
        if (this.refs.dialog?.open) this.closeDialog();
      },
    };

    if (frameId) params.frame = frameId;
    if (initialCarousel) params.carousel = initialCarousel;

    this.#carouselKey = nextKey;
    this.#fitmixReady = false;
    this.#fitmix = FitMix.createWidget(FITMIX_CONTAINER_ID, params, () => {
      this.#fitmixReady = true;
      this.#readyQueue.splice(0).forEach((resolve) => resolve());
    });
  }

  #whenFitMixReady() {
    if (this.#fitmixReady) return Promise.resolve();
    return new Promise((resolve) => {
      this.#readyQueue.push(resolve);
    });
  }

  /**
   * @param {string} frameId
   * @param {object | null} carousel
   */
  #startExperience(frameId, carousel) {
    if (!this.#fitmix) return;

    if (carousel && typeof this.#fitmix.setCarousel === 'function') {
      this.#fitmix.setCarousel(carousel);
    }

    this.#fitmix.setFrame(String(frameId));
    this.#fitmix.startVto('live');
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (!this.refs.dialog?.open || !this.#fitmix) return;
    const variantId = event.detail?.resource?.id;
    const frameId = variantId != null ? this.#variantFrameIds[variantId] : null;
    if (!frameId) return;
    this.#fitmix.setFrame(String(frameId));
  };

  #onDialogClose = () => {
    this.#openGeneration += 1;
    if (!this.#fitmix) return;

    this.#closing = true;
    try {
      this.#fitmix.stopVto();
    } catch (_) {}
    this.#closing = false;
    this.#releaseCamera();
    this.#destroyWidget();
  };

  #destroyWidget() {
    try {
      this.#fitmix?.remove?.();
    } catch (_) {}
    this.#fitmix = null;
    this.#fitmixReady = false;
    this.#readyQueue = [];
    this.#carouselKey = '';
    const container = this.querySelector(`#${FITMIX_CONTAINER_ID}`);
    if (container) container.innerHTML = '';
  }

  #releaseCamera() {
    activeStreams.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
    activeStreams.clear();

    const collectVideos = (root) => {
      const videos = [...root.querySelectorAll('video')];
      root.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) videos.push(...collectVideos(el.shadowRoot));
      });
      return videos;
    };

    collectVideos(this).forEach((video) => {
      const stream = video.srcObject;
      if (stream && typeof stream.getTracks === 'function') {
        stream.getTracks().forEach((track) => track.stop());
      }
      video.srcObject = null;
    });
  }
}

if (!customElements.get('vto-trigger')) customElements.define('vto-trigger', VtoTrigger);
if (!customElements.get('vto-dialog')) customElements.define('vto-dialog', VtoDialog);
